import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Box } from '@mui/material';
import { PRIMARY_COLOR } from './constants';
import { tokens } from "../../theme/tokens";

interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
  taskTitle: string;
  onSubmit: (note: string) => void;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({ open, onClose, taskTitle, onSubmit }) => {
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    if (!open) setNoteText('');
  }, [open]);

  const canSubmit = noteText.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(noteText.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <Box sx={{ px: '20px', pt: '16px', pb: '4px' }}>
        <Box sx={{ fontSize: '14px', fontWeight: 600, color: tokens.color.text.primary }}>Add Note</Box>
        <Box sx={{ fontSize: '12px', color: tokens.color.text.secondary, mt: '2px' }}>{taskTitle}</Box>
      </Box>

      <DialogContent sx={{ pt: '12px !important' }}>
        <textarea
          autoFocus
          aria-label="Note text"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Write a note…"
          rows={4}
          style={{
            display: 'block',
            width: '100%',
            fontSize: '13px',
            padding: '8px 10px',
            border: `1px solid ${tokens.color.border.strong}`,
            borderRadius: '7px',
            outline: 'none',
            resize: 'vertical',
            color: tokens.color.text.primary,
            boxSizing: 'border-box',
            lineHeight: 1.6,
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: '7px',
            padding: '7px 16px',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: tokens.color.surface.default,
            backgroundColor: canSubmit ? PRIMARY_COLOR : tokens.color.text.secondary,
            border: 'none',
            borderRadius: '7px',
            padding: '7px 16px',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          Add Note
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNoteModal;
